import { Outlet, Link, createRootRouteWithContext, HeadContent, Scripts } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import appCss from "../styles.css?url";
import tkCss from "../components/tk/tk.css?url";
import { useRouterState } from "@tanstack/react-router";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { GrainOverlay } from "../components/site/primitives";
import { ChefBuddyMascot } from "../components/mascot/ChefBuddyMascot";

/** Fonts are self-hosted in /public/fonts and declared in styles.css. */


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4 text-bone">
      <div className="max-w-md text-center">
        <p className="font-script text-5xl text-ember">lost</p>
        <h1 className="mt-2 h-display text-7xl">404</h1>
        <p className="mt-3 text-sm text-bone/60">
          This corner doesn't exist — yet.
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-ember px-6 py-3 font-display text-sm font-semibold text-bone transition-colors hover:bg-ember/90"
          >
            Return home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "thatKorner — The World's First Native Ai Dining Concierge" },
      {
        name: "description",
        content:
          "thatKorner is an Ai-powered hospitality-tech brand transforming menus into intelligent, conversational experiences for luxury restaurants and hotels.",
      },
      { name: "author", content: "KARV Ventures LLC" },
      { property: "og:title", content: "thatKorner — The World's First Native Ai Dining Concierge" },
      {
        property: "og:description",
        content: "Beyond the QR code. Invisible luxury for modern hospitality.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@thatKorner" },
      { name: "theme-color", content: "#000000" },
      { name: "twitter:title", content: "thatKorner — The World's First Native Ai Dining Concierge" },
      { name: "twitter:description", content: "Beyond the QR code. Invisible luxury for modern hospitality." },
      { property: "og:site_name", content: "thatKorner" },
      { property: "og:url", content: "https://www.thatkorner.com/" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "stylesheet", href: tkCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600&family=Bebas+Neue&family=Caladea:ital,wght@0,400;0,700;1,400&family=JetBrains+Mono:wght@400;500&display=swap",
      },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/icon-48.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/icon-96.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      // Fonts are self-hosted with `font-display: swap` and intentionally NOT
      // preloaded — preloading them competed with the hero image on slow
      // connections and pushed FCP/LCP out.

    ],
    scripts: [


      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "thatKorner",
          url: "https://www.thatkorner.com",
          logo: "https://www.thatkorner.com/icon-512.png",
          image: "https://www.thatkorner.com/brand-logo.png",
          description:
            "AI-powered hospitality-tech brand transforming menus into intelligent, conversational experiences for luxury restaurants and hotels.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "thatKorner",
          alternateName: ["That Korner", "thatkorner.com"],
          url: "https://www.thatkorner.com",
        }),
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="bg-ink text-bone">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

import { useEffect } from "react";
import { captureReferralCodeFromUrl } from "../lib/affiliate";

const TK_ROUTES = new Set(["/", "/chef-buddy", "/chef-brain", "/korner-ask", "/platform", "/trust", "/affiliate"]);

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    captureReferralCodeFromUrl();
  }, []);

  if (TK_ROUTES.has(pathname.replace(/\/$/, "") || "/")) {
    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <ChefBuddyMascot />
      </QueryClientProvider>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <GrainOverlay />
      <SiteHeader />
      <main className="relative z-[2]">
        <Outlet />
      </main>
      <SiteFooter />
      <ChefBuddyMascot />
    </QueryClientProvider>
  );
}
